import { GeofenceEnforcedAction, GeofenceStatus } from 'app/API.service.custom';

/**
 * Geofence domain types for the admin UI.
 *
 * Two coordinate conventions live in this feature and they are NOT the same:
 *
 *  - GeoJSON (`GeoJsonPosition`) is `[longitude, latitude]`. This is what the
 *    backend stores and what leaves/enters this app.
 *  - Google Maps (`LatLngPoint`) is `{ lat, lng }`. This is what the map editor
 *    works in.
 *
 * The conversion lives in exactly one place (`geofence-geometry.ts`) so the
 * lat/lng transposition trap has a single home.
 */

/** GeoJSON position: `[longitude, latitude]`. */
export type GeoJsonPosition = [number, number];

/** GeoJSON linear ring — closed (first position repeated as last), >= 4 positions. */
export type GeoJsonLinearRing = GeoJsonPosition[];

/** `[exteriorRing, ...interiorRings]` — interior rings are holes. */
export type GeoJsonPolygonCoordinates = GeoJsonLinearRing[];

export interface GeoJsonPolygon {
  type: 'Polygon';
  coordinates: GeoJsonPolygonCoordinates;
}

export interface GeoJsonMultiPolygon {
  type: 'MultiPolygon';
  coordinates: GeoJsonPolygonCoordinates[];
}

/** The only two geometry shapes a geofence may carry. */
export type GeofenceGeometry = GeoJsonPolygon | GeoJsonMultiPolygon;

/** Google-Maps-native point used by the editor. */
export interface LatLngPoint {
  lat: number;
  lng: number;
}

/** An open ring (no repeated closing point) in map-native coordinates. */
export type EditableRing = LatLngPoint[];

/**
 * One drawable region inside a geofence: an outer boundary plus any number of
 * excluded areas (holes) carved out of it.
 *
 * A geofence holds MANY of these. "Add another area" for a member means adding
 * an area here — never a second geofence (a member has exactly one).
 */
export interface GeofenceArea {
  /** Client-side identity only; never sent to the server. */
  id: string;
  outer: EditableRing;
  holes: EditableRing[];
}

/**
 * `GeofenceView` with its geometry parsed.
 *
 * Two ids, and they are not interchangeable:
 *  - `id` identifies THIS revision, and is what `AccountMembership.operatingGeofenceId`
 *    points at (which is why a revise has to re-point assigned members).
 *  - `logicalGeofenceId` is stable across revisions, and is what every write
 *    addresses — revise, archive and assign all take it, never `id`.
 */
export interface Geofence {
  id: string;
  logicalGeofenceId: string;
  accountId: string;
  name: string;
  /** Monotonic revision number. Geometry is immutable per version. */
  version: number;
  status: GeofenceStatus;
  geometry: GeofenceGeometry;
  supersededById?: string | null;
  /** Server-side rollup; the UI prefers its own count off the membership list. */
  assignedMemberCount?: number | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

/**
 * A superseded revision, for the read-only history panel. There is no dedicated
 * revisions query — these are the SUPERSEDED rows from
 * `listAccountGeofences(includeSuperseded: true)`, grouped by `logicalGeofenceId`.
 */
export type GeofenceRevision = Geofence;

/** A member row as the geofence screens need it. */
export interface GeofenceMember {
  /** `AccountMembership.id` — the id assignment writes against. */
  membershipId: string;
  userId: string;
  name: string;
  designation?: string | null;
  authorizationLevel?: string | null;
  userAccountStatus?: string | null;
  profilePictureUrl?: string | null;
  operatingGeofenceId?: string | null;
  geofenceEnforcedActions: GeofenceEnforcedAction[];
}

export interface GeofenceEnforcedActionOption {
  value: GeofenceEnforcedAction;
  label: string;
  /** What is actually denied when the member is outside the geofence. */
  description: string;
}

/**
 * The five per-MEMBER enforced actions. Enforcement is never configured on the
 * geofence itself — two members can share one geofence and enforce differently.
 */
export const GEOFENCE_ENFORCED_ACTION_OPTIONS: readonly GeofenceEnforcedActionOption[] = [
  {
    value: GeofenceEnforcedAction.ATTENDANCE_CHECK_IN,
    label: 'Attendance check-in',
    description: 'Cannot start the day from outside the geofence.',
  },
  {
    value: GeofenceEnforcedAction.ATTENDANCE_CHECK_OUT,
    label: 'Attendance check-out',
    description: 'Cannot end the day from outside the geofence.',
  },
  {
    value: GeofenceEnforcedAction.VISIT_CHECK_IN,
    label: 'Visit check-in',
    description: 'Cannot start a lead visit from outside the geofence.',
  },
  {
    value: GeofenceEnforcedAction.VISIT_CHECK_OUT,
    label: 'Visit check-out',
    description: 'Cannot close a lead visit from outside the geofence.',
  },
  {
    value: GeofenceEnforcedAction.RETROSPECTIVE_VISIT_LOG,
    label: 'Log a past visit',
    description: 'Cannot back-date a visit that happened outside the geofence.',
  },
] as const;

export function enforcedActionLabel(action: GeofenceEnforcedAction | string): string {
  return GEOFENCE_ENFORCED_ACTION_OPTIONS.find(o => o.value === action)?.label ?? String(action);
}
